'use client';

import { useEffect, useState } from 'react';

/**
 * Hook to detect virtual keyboard visibility and track visual viewport dimensions
 *
 * Uses the Visual Viewport API to detect when the mobile keyboard is open.
 * This is useful for conditional UI rendering or debugging keyboard behavior.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Visual_Viewport_API
 *
 * Browser support:
 * - Chrome/Edge: 61+
 * - Safari: 13+
 * - Firefox: 91+
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { keyboardVisible, viewportHeight } = useVisualViewport();
 *
 *   return (
 *     <div>
 *       {keyboardVisible && <p>Keyboard is open</p>}
 *       <p>Visual viewport height: {viewportHeight}px</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useVisualViewport() {
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );

  useEffect(() => {
    // Check if Visual Viewport API is available
    if (!window.visualViewport) {
      console.warn('Visual Viewport API not supported in this browser');
      return;
    }

    const handleResize = () => {
      const visualHeight = window.visualViewport!.height;
      const layoutHeight = window.innerHeight;

      setViewportHeight(visualHeight);

      // If visual viewport is significantly smaller than layout viewport,
      // the keyboard is likely open. Using 100px threshold to account for
      // browser UI changes (address bar, etc.)
      const heightDifference = layoutHeight - visualHeight;
      setKeyboardVisible(heightDifference > 100);
    };

    // Listen to both resize and scroll events
    // Scroll can fire when keyboard opens/closes on some browsers
    window.visualViewport.addEventListener('resize', handleResize);
    window.visualViewport.addEventListener('scroll', handleResize);

    // Get initial state
    handleResize();

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);

  return {
    /** Whether the virtual keyboard is currently visible */
    keyboardVisible,
    /** Current visual viewport height in pixels */
    viewportHeight,
  };
}
